/**
 * <ReportsLauncherDrawer /> — the Operations-side contextual report
 * launcher per M3X.REPORTS §0.9.A.
 *
 * Every Operations sub-domain hub page has a `Reports →` button in
 * its action bar that opens this drawer. The drawer slides in from
 * the right (mirrored in RTL via logical properties) and shows
 * every report for the requested domain, with a search box and
 * category chips.
 *
 * Spec lock:
 *   - Width: 400px (sm+); full sheet on mobile
 *   - Search: 300ms debounce; matches title + description
 *   - Category chips: All + present categories in the domain
 *   - Per-card: dot indicator (category color), title, 1-line
 *     description, Run report → button
 *   - Click `Run report`: navigates to /reports/{domain}/{id} →
 *     drawer closes
 *   - Close: × button, Esc, click outside
 *
 * Reuses card markup with <ReportsDomainIndex /> for consistency.
 */

import { ArrowRight, FileText, Sparkles, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import {
  type ReportCategory,
  type ReportDomain,
  categoriesForDomain,
  reportUrl,
  reportsForDomain,
} from "../../lib/reports/catalog";

export interface ReportsLauncherDrawerProps {
  domain: ReportDomain;
  /** Localized domain title — drawer header. */
  domainLabel: string;
  /** Localized 1-line summary line below the title. */
  domainHint?: string;
  open: boolean;
  onClose: () => void;
}

const CATEGORY_LABEL_KEYS: Record<ReportCategory, string> = {
  operational: "reports.cat_operational",
  compliance: "reports.cat_compliance",
  cost: "reports.cat_cost",
  trend: "reports.cat_trend",
  analytics: "reports.cat_analytics",
};

const CATEGORY_DOT_CLASS: Record<ReportCategory, string> = {
  operational: "bg-brand-teal",
  compliance: "bg-rose-500",
  cost: "bg-brand-blue",
  trend: "bg-brand-purple",
  analytics: "bg-brand-deep",
};

export function ReportsLauncherDrawer({
  domain,
  domainLabel,
  domainHint,
  open,
  onClose,
}: ReportsLauncherDrawerProps) {
  const { t } = useTranslation();
  const all = useMemo(() => reportsForDomain(domain), [domain]);
  const cats = useMemo(() => categoriesForDomain(domain), [domain]);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [activeCat, setActiveCat] = useState<ReportCategory | "all">("all");
  const closeRef = useRef<HTMLButtonElement | null>(null);

  // Debounce search input by 300ms per spec.
  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedQuery(query), 300);
    return () => window.clearTimeout(handle);
  }, [query]);

  // Escape closes; focus the close button when the drawer opens.
  useEffect(() => {
    if (!open) return undefined;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const filtered = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    return all.filter((r) => {
      if (activeCat !== "all" && r.category !== activeCat) return false;
      if (!q) return true;
      const title = t(r.titleKey, r.titleKey).toLowerCase();
      const desc = t(r.descriptionKey, r.descriptionKey).toLowerCase();
      return title.includes(q) || desc.includes(q);
    });
  }, [all, debouncedQuery, activeCat, t]);

  if (!open) return null;

  return (
    <>
      {/* Click-outside backdrop */}
      <div
        aria-hidden
        onClick={onClose}
        className="fixed inset-0 z-40 bg-black/10"
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={t("reports.launcher_aria_label", "{{domain}} reports launcher", {
          domain: domainLabel,
        })}
        className="fixed inset-y-0 end-0 z-50 flex w-full max-w-[400px] flex-col bg-surface-default shadow-2xl"
      >
        <header className="flex items-start justify-between gap-2 border-b border-border-subtle p-4">
          <div className="flex-1">
            <div className="flex items-center gap-1 text-base font-semibold text-brand-deep">
              <Sparkles
                size={14}
                aria-hidden
                className="text-brand-teal"
              />
              {t("reports.launcher_title", "{{domain}} reports", {
                domain: domainLabel,
              })}
            </div>
            {domainHint && (
              <p className="mt-1 text-xs text-text-tertiary">{domainHint}</p>
            )}
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label={t("app.close", "Close")}
            className="rounded-sm p-1 text-text-tertiary hover:bg-surface-sunken hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
          >
            <X size={16} aria-hidden />
          </button>
        </header>

        <div className="flex flex-col gap-2 border-b border-border-subtle p-4">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("reports.search_placeholder", "Search reports…")}
            className="w-full rounded-sm border border-border bg-surface-default px-3 py-1.5 text-sm placeholder:text-text-tertiary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
          />
          <div className="flex flex-wrap items-center gap-1">
            <CategoryChip
              label={t("reports.cat_all", "All")}
              active={activeCat === "all"}
              onClick={() => setActiveCat("all")}
            />
            {cats.map((cat) => (
              <CategoryChip
                key={cat}
                label={t(CATEGORY_LABEL_KEYS[cat], cat)}
                dotClass={CATEGORY_DOT_CLASS[cat]}
                active={activeCat === cat}
                onClick={() => setActiveCat(cat)}
              />
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {filtered.length === 0 ? (
            <div className="rounded-md border border-dashed border-border-subtle bg-surface-sunken p-4 text-center text-xs text-text-tertiary">
              <FileText size={20} aria-hidden className="mx-auto mb-1.5" />
              {t("reports.empty_launcher", "No reports match")}
            </div>
          ) : (
            <ul className="flex flex-col gap-2">
              {filtered.map((r) => (
                <li
                  key={r.id}
                  className="rounded-md border border-border-subtle bg-surface-default p-3 hover:border-brand-blue/40"
                >
                  <div className="flex items-center gap-1.5">
                    <span
                      aria-hidden
                      className={`h-2 w-2 rounded-full ${CATEGORY_DOT_CLASS[r.category]}`}
                    />
                    <span className="text-[10px] uppercase tracking-wide text-text-tertiary">
                      {t(CATEGORY_LABEL_KEYS[r.category], r.category)}
                    </span>
                  </div>
                  <h3 className="mt-1 text-sm font-medium text-text-primary">
                    {t(r.titleKey, r.titleKey)}
                  </h3>
                  <p className="mt-0.5 text-xs text-text-tertiary leading-relaxed">
                    {t(r.descriptionKey, r.descriptionKey)}
                  </p>
                  {r.shipped ? (
                    <Link
                      to={reportUrl(r.domain, r.id)}
                      onClick={onClose}
                      className="mt-2 inline-flex items-center gap-1 rounded-sm bg-brand-blue px-2.5 py-1 text-xs font-medium text-white hover:bg-brand-deep"
                    >
                      {t("reports.run_report", "Run report")}
                      <ArrowRight size={11} aria-hidden className="rtl:rotate-180" />
                    </Link>
                  ) : (
                    <span className="mt-2 inline-block text-[10px] text-text-tertiary italic">
                      {t("reports.coming_label", "Coming soon")}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
    </>
  );
}

function CategoryChip({
  label,
  dotClass,
  active,
  onClick,
}: {
  label: string;
  dotClass?: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px]",
        active
          ? "border-brand-blue bg-brand-blue/10 text-brand-deep"
          : "border-border-subtle text-text-secondary hover:bg-surface-sunken",
      ].join(" ")}
    >
      {dotClass && (
        <span aria-hidden className={`h-1.5 w-1.5 rounded-full ${dotClass}`} />
      )}
      {label}
    </button>
  );
}

export default ReportsLauncherDrawer;
